/**
 * A checklist code from the follow-up email (core leads.ts; docs/LEADS.md). The email's sign-up link is /app/?signup=1&lead=CC10-…:
 * keep the code until the person starts their plan, show "10% off your first month" only when the server says the code works,
 * and hand it to Checkout. A wrong or used code changes nothing and blocks nothing.
 */
import { normalizeLeadCode } from "@logwave/core";
import { config } from "../env.ts";

export const LEAD_KEY = "cc.lead";

function safeStorage(): Storage | undefined {
  try { return typeof localStorage === "undefined" ? undefined : localStorage; } catch { return undefined; }
}

/** A visit from the email lands with ?lead=: keep it. Returns the code, or null. */
export function captureLeadCode(search: string, storage: Storage | undefined = safeStorage()): string | null {
  const code = normalizeLeadCode(new URLSearchParams(search).get("lead") ?? "");
  if (!code) return null;
  try { storage?.setItem(LEAD_KEY, code); } catch { /* private mode: the code just isn't kept */ }
  return code;
}
export function storedLeadCode(storage: Storage | undefined = safeStorage()): string | null {
  try { return normalizeLeadCode(storage?.getItem(LEAD_KEY) ?? ""); } catch { return null; }
}
export function clearLeadCode(storage: Storage | undefined = safeStorage()): void {
  try { storage?.removeItem(LEAD_KEY); } catch { /* ignore */ }
}

/** Asks the server whether the code takes 10% off a first month right now. Any failure means "no": the line just isn't shown. */
export async function leadOffer(code: string, fetchFn: typeof fetch = fetch, base: string = config.apiUrl): Promise<{ percent: number; months: number } | null> {
  try {
    const res = await fetchFn(`${base}/leads/code?c=${encodeURIComponent(code)}`);
    if (!res.ok) return null;
    const j = await res.json() as { valid?: boolean; percent?: number; months?: number };
    return j.valid && typeof j.percent === "number" ? { percent: j.percent, months: j.months ?? 1 } : null;
  } catch { return null; }
}
